import { useState, useEffect, useCallback } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";
import { api } from "@/lib/api";
import type { PipelineItem } from "@/lib/types";
import { SITE_IMAGES } from "@/lib/siteAssets";

const tabs = [
  { id: "greeting", label: "Greeting" },
  { id: "vision", label: "Vision" },
  { id: "initiation", label: "Initiation" },
  { id: "pipeline", label: "Focused Subject & Pipeline" },
];

export default function About() {
  const [activeTab, setActiveTab] = useState("greeting");
  const [pipelineItems, setPipelineItems] = useState<PipelineItem[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const res = await api.get<PipelineItem[]>("/public/pipeline");
      if (res.data) setPipelineItems(res.data);
    } catch {
      // 로딩 실패 시 빈 목록 유지
    }
    setPipelineLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === "pipeline" && pipelineItems.length === 0) {
      fetchPipeline();
    }
  }, [activeTab, pipelineItems.length, fetchPipeline]);

  return (
    <div className="min-h-screen bg-white pt-[96px]">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        <img
          src={SITE_IMAGES.pageBanner}
          alt="About Biocon"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl md:text-5xl font-light text-white tracking-wide mb-2">
            THE TARGET FACTORY
          </h1>
          <p className="text-base md:text-xl text-white/90 font-light">
            Paving A Road for Novel Drug Discovery
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-[#f6f9fc] border-b border-gray-100">
        <div className="container mx-auto px-4 py-3">
          <nav className="text-sm text-gray-500">
            <a href="/" className="hover:text-[#1977cc] transition-colors">Home</a>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-[#2c4964] font-medium">About Us</span>
          </nav>
        </div>
      </div>

      {/* Tabs */}
      <section className="bg-[#f6f9fc] pt-8 pb-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-[#1977cc] text-white shadow-sm"
                    : "bg-white text-[#2c4964] hover:bg-[#1977cc]/10 border border-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-10 md:py-16">
        <div className="container mx-auto px-4">
          {activeTab === "greeting" && (
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#2c4964] text-center mb-10">
                  Greeting
                </h2>
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="w-full md:w-[280px] flex-shrink-0">
                    <img
                      src={SITE_IMAGES.greetingDirector}
                      alt="Director Sunghoon Kim"
                      className="w-full rounded-lg"
                    />
                  </div>
                  <div className="flex-1 text-gray-600 leading-relaxed text-[15px]">
                    <p className="mb-4">
                      Medicinal Bioconvergence Research Center (Biocon) was initiated in 2010 as one of the Global Frontier Projects that are by Ministry of Science, ICT and Future Planning. Our goal is to build an integrated research platform namely &ldquo;The Target Factory&rdquo; to develop well-validated packages of targets and leads that can be used in biotech and pharmaceutical industry. Biocon consists of the experts in target identification, drug design, drug screening and disease modeling. The results from these four specialist groups are converged into integration core group. The matured packages of target and lead are then relayed to the group for translation and development (named TRADE). We will do our best to establish Biocon the world&apos;s first and best target factory that systematically translate original science and technology to new therapy and diagnosis for future medicine.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                      <span className="text-[#2c4964] font-medium">Sunghoon Kim Ph.D.</span>
                      <img
                        src={SITE_IMAGES.greetingSignature}
                        alt="Signature"
                        className="h-8 w-auto"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Director, Biocon / Professor, Yonsei University, Republic of Korea.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          )}

          {activeTab === "vision" && (
            <ScrollReveal>
              <div className="max-w-5xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#2c4964] text-center mb-10">
                  Vision
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="flex items-start">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#2c4964] leading-snug">
                      Vision :<br />
                      An Integrated<br />
                      Research Platform for<br />
                      Novel Target and<br />
                      Lead Discovery
                    </h3>
                  </div>
                  <div className="text-gray-600 leading-relaxed text-[15px]">
                    <p className="mb-4">
                      The Medicinal Bioconvergence Research Center of Yonsei University (or Biocon) has set a challenge to establish an &ldquo;innovative research platform for drug discovery&rdquo; that can significantly reduce the time and cost while improving the success probability of drug discovery.
                    </p>
                    <p className="mb-4">
                      Among the many different stages required for drug development, Biocon focuses on the &ldquo;identification and validation of novel drug targets&rdquo;. Although well-validated druggable targets are critical determinants for the success of drug discovery, target discovery is typically a function that pharma or biotech industry launch in-house, because it involves much basic science and there are no standard operation procedures for this process. Therefore, druggable targets have to be provided by the researchers in academia and public sectors. Biocon was established to fill the gap between academia and industry by maturing potentially druggable targets.
                    </p>
                    <p>
                      The identification of novel targets involves a broad spectrum of research activity from in-depth basic biology to translation. The strength of Biocon is based on the integration of biology and chemistry teams combined with cutting-edge convergence technologies. Through this fully united research teamwork, Biocon is aiming to become the world&apos;s first and best target factory, providing innovative targets to the pharmaceutical industry.
                    </p>
                  </div>
                  <div className="flex items-start justify-center md:justify-end">
                    <p className="text-xl md:text-2xl italic text-[#1977cc] font-light leading-relaxed text-center md:text-right">
                      &ldquo;Innovative research<br />
                      platform for drug<br />
                      discovery&rdquo;
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          )}

          {activeTab === "initiation" && (
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#2c4964] text-center mb-10">
                  Initiation : Searching for Novel Targets
                </h2>
                <div className="text-gray-600 leading-relaxed text-[15px] mb-10">
                  <p className="mb-4">
                    The need for new drugs is ever increasing due to the surge in diseases such as cancer, diabetes and dementia that are mainly caused by extended life span. However, the number of newly approved drugs is continuously decreasing, giving a crisis not only for drug industry but also eventually for human health. In 2010, the Korean Ministry of Education, Science and Technology (MEST) launched the Global Frontier Project, targeted at solving global challenges in major R&amp;D areas. The Medicinal Bioconvergence Research Center (Biocon) was initiated as one of the three projects with the aim of resolving difficulties during the development of novel drug discovery. This project will proceed for an initial nine years from 2010 to 2019, and is divided into three stages (1st: 2 years, 2nd: 3 years, 3rd: 4 years). With the financial support of about 140 million dollars and a projected time-line of 9 years, Biocon has leveraged a unique capability to discover and validate molecular pathways and targets at a level that is possible at few commercial enterprises.
                  </p>
                </div>
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    src="https://www.youtube.com/embed/0J_KCtakGCw?feature=oembed&wmode=opaque&showinfo=0"
                    title="World's Best Target Factory - Biocon"
                    className="absolute inset-0 w-full h-full rounded-lg"
                    allowFullScreen
                  />
                </div>
              </div>
            </ScrollReveal>
          )}

          {activeTab === "pipeline" && (
            <ScrollReveal>
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-semibold text-[#2c4964] text-center mb-4">
                  Focused Subject &amp; Pipeline
                </h2>
                <div className="text-gray-600 leading-relaxed text-[15px] max-w-4xl mx-auto text-center mb-8">
                  <p className="mb-4">
                    Biocon focuses on human aminoacyl-tRNA synthetases (ARSs) and their interacting proteins as our primary target space of interest, and explores their potential to address diverse human diseases. These enzymes emerged early in evolution and catalytically link their cognate amino acids to tRNAs for protein synthesis. For this reason, ARSs have been traditionally considered as housekeeping proteins solely dedicated to protein synthesis. However, recent new findings from Biocon and other groups world-wide on the novel functions of ARSs are rapidly changing the classical view of these enzymes. Indeed ARSs are emerging as central coordinators linking cell fate determination processes with protein synthesis.
                  </p>
                </div>

                <div className="flex items-center justify-center mb-8">
                  <div className="h-px w-12 bg-gray-300" />
                  <span className="mx-4 text-sm text-gray-500 font-medium uppercase tracking-wider">
                    Oncology and other complex diseases
                  </span>
                  <div className="h-px w-12 bg-gray-300" />
                </div>

                {/* Pipeline Cards Grid */}
                {pipelineLoading ? (
                  <div className="text-center py-12 text-gray-400">Loading pipeline...</div>
                ) : pipelineItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">No pipeline items found.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pipelineItems.map((item) => (
                      <div
                        key={item.id}
                        className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden"
                      >
                        <div className="relative h-48 bg-[#f6f9fc] overflow-hidden">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <i className="ri-image-line text-3xl" />
                            </div>
                          )}
                        </div>
                        <div className="p-5">
                          <h3 className="text-sm font-semibold text-[#2c4964] leading-snug mb-2 line-clamp-2">
                            {item.name}
                          </h3>
                          {item.target && (
                            <p className="text-xs text-gray-500 leading-relaxed mb-2 whitespace-pre-line line-clamp-3">
                              {item.target}
                            </p>
                          )}
                          {item.indications && (
                            <p className="text-xs text-gray-400 mb-3 whitespace-pre-line">
                              {item.indications}
                            </p>
                          )}
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#e8f4fd] text-[#1977cc]">
                            {item.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-center mt-10">
                  <a
                    href="/product"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#1977cc] text-white rounded-md text-sm font-medium hover:bg-[#1669b3] transition-colors whitespace-nowrap"
                  >
                    View More Pipeline
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            </ScrollReveal>
          )}
        </div>
      </section>
    </div>
  );
}